import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { NAV_LINKS } from "@/data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On subpages, hash links must first return to the home route.
  const hrefFor = (hash) => (isHome ? hash : `/${hash}`);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? "bg-brand-cream/85 backdrop-blur-md border-b border-brand-peach" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-brand-apricot" />
          <span className="font-display text-2xl tracking-tight text-brand-ink">PeachFlow</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-teal mt-2 hidden sm:block">
            AI
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={hrefFor(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-mono text-xs uppercase tracking-[0.15em] text-brand-ink/70 hover:text-brand-teal transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
          <a
            href={hrefFor("#book")}
            data-testid="nav-book-call"
            className="rounded-full bg-brand-teal text-brand-cream px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] hover:bg-brand-ink transition-colors duration-300"
          >
            Book a Call
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-brand-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-brand-cream border-b border-brand-peach"
          >
            <div className="flex flex-col px-6 py-6 gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={hrefFor(l.href)}
                  onClick={() => setOpen(false)}
                  className="font-mono text-sm uppercase tracking-[0.15em] text-brand-ink"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={hrefFor("#book")}
                onClick={() => setOpen(false)}
                data-testid="nav-book-call-mobile"
                className="rounded-full bg-brand-teal text-brand-cream px-5 py-3 text-center font-mono text-xs uppercase tracking-[0.15em]"
              >
                Book a Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
